import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import paymentService from "api/payment/payment.service";
import { useSnackbar } from "hooks/useSnackbar";

/**
 * Opens the Stripe Billing Portal with a session fetched AT CLICK TIME (STRIPE P6 F-d).
 *
 * A portal url fetched at page load stays valid for the whole Stripe session, so a team suspended after the page loaded
 * still reached the MAIN portal, and the click never told the backend. Asking `GET /payments/manage_portal` on every
 * click lets the backend refuse it NOW: a `403 team_suspended` resolves `_handled` after the interceptor has already
 * raised the suspended-team screen ([HTTP_HANDLED_RESPONSE]), so nothing opens and nothing else is shown.
 *
 * The portal opens in THIS tab: a `window.open` after an `await` is a popup most browsers block, and the portal's own
 * return link brings the customer back.
 *
 * `onError` lets a surface show the failure inline (PastDueModal sits above the snackbar); by default it is a snackbar.
 */
export const useBillingPortal = ({ onError }: { onError?: (message: string) => void } = {}) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [isOpening, setIsOpening] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    mounted.current = true;
    // Back from the portal can restore this page from the back/forward cache exactly as it was left — busy, with the
    // click guard still held. A restored page gets a usable button again.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      inFlight.current = false;
      setIsOpening(false);
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      mounted.current = false;
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const report = useCallback((message: string) => (onErrorRef.current ? onErrorRef.current(message) : showError(message)), [showError]);

  const openPortal = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsOpening(true);
    // Once the browser is leaving for the portal the button stays busy — a second click would only mint a second session.
    let navigating = false;

    try {
      const res = await paymentService.managePortal();
      // The interceptor owns 401/403/409/503 (team_suspended included) and has already acted.
      if (res?._handled) return;

      const url = res?.responseData?.portal_url;
      if (!url) {
        report(t("billing:past_due_modal.error_portal_missing"));

        return;
      }

      navigating = true;
      window.location.assign(url);
    } catch {
      report(t("billing:past_due_modal.error_portal_missing"));
    } finally {
      if (!navigating) {
        inFlight.current = false;
        if (mounted.current) setIsOpening(false);
      }
    }
  }, [report, t]);

  return { openPortal, isOpening };
};
